// NavHost — the activity rail's impure shell (host extraction, mirroring
// IngestHost/OmnibarHost/EditorHost's strangler-fig pattern applied to the
// left-nav zone). It owns ONE piece of session-local chrome: which category
// panel is showing (`activeCategory`) — like NavRail's old `collapsedSections`
// was, now a single value instead of a per-section set (one panel at a time,
// design doc §4).
//
// The rail's Overview panel and its data shaping were RETIRED (owner-settled
// 2026-08-19): the landing is the pinned Home tab in the centre strip now, so
// the rail is purely the object browser. The "what's here / needs judgement /
// resume" view-model that used to live here is gone with it — the Home tab's
// `WorkspaceLanding` sources its own data from the loaded library directly.
//
// `useNavHost` is a HOOK (not a mounted component, like OmnibarHost): the
// rail's own DOM lives in `ActivityRail.tsx`, seated by AppShellHost exactly
// where NavRail used to be — a component wrapping this hook's output would
// need a render-prop just to hand ActivityRail its own props, more
// indirection than a hook AppShellHost destructures straight into the JSX.

import { useState } from "react";
import type {
    ExportRef,
    MaterialRef,
    RailCategory,
    SourceRef,
} from "../Navigation/types.js";

export type NavHostProps = {
    // The library's kind-filtered refs — already derived once by AppShellHost
    // from `library` (the same `refsOfLibrary` split every other surface
    // reads); passed in rather than re-filtered here.
    readonly sources: readonly SourceRef[];
    readonly materials: readonly MaterialRef[];
    readonly packages: readonly ExportRef[];
};

export type NavHostResult = {
    // `null` means no panel is open — the rail shows only the icon column
    // (VS Code activity-bar model: an icon click TOGGLES its panel, so
    // "nothing selected" is a real, reachable state, not just an initial
    // default).
    readonly activeCategory: RailCategory | null;
    readonly selectCategory: (category: RailCategory) => void;
    // Distinct from `selectCategory` (own intent, own name): closes
    // whichever panel is open. Never expressed as `selectCategory(null)` —
    // "switch to category X" and "close the open panel" are different
    // commands, not one flag-toggling call.
    readonly closeCategory: () => void;
    readonly sources: readonly SourceRef[];
    readonly materials: readonly MaterialRef[];
    readonly packages: readonly ExportRef[];
};

export const useNavHost = ({
    sources,
    materials,
    packages,
}: NavHostProps): NavHostResult => {
    // The rail's own switch — session-local. CLOSED at rest (owner call
    // 2026-08-20): a fresh session lands on the calm landing with no panel
    // open — the rail is a bare icon column until an icon is clicked. `null`
    // means no panel (the same state the author reaches by closing an open
    // one). The bare rail-reveal fallback ("materials") lives at the host edge
    // (`revealRail` in AppShellHost), not here.
    const [activeCategory, setActiveCategory] = useState<RailCategory | null>(
        null,
    );

    return {
        activeCategory,
        selectCategory: setActiveCategory,
        closeCategory: () => setActiveCategory(null),
        sources,
        materials,
        packages,
    };
};
